import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import CtaBand from "@/components/site/CtaBand";
import Breadcrumbs, { breadcrumbJsonLd } from "@/components/site/Breadcrumbs";
import { COLLECTIONS, pageUrl } from "@/data/seoPages";
import { useSeo } from "@/lib/useSeo";

const SITE = "https://anderstongroup.co.uk";

export default function CollectionPage({ collection }) {
  const meta = COLLECTIONS[collection];
  const crumbs = useMemo(() => [{ name: "Home", to: "/" }, { name: meta.label, to: meta.base }], [meta]);

  const seo = useMemo(
    () => ({
      title: meta.title,
      description: meta.description,
      path: meta.base,
      image: meta.items[0].image,
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "CollectionPage",
            "@id": `${SITE}${meta.base}#page`,
            name: meta.title,
            description: meta.description,
            url: `${SITE}${meta.base}`,
            isPartOf: { "@id": `${SITE}/#website` },
            hasPart: meta.items.map((p) => ({ "@type": "WebPage", name: p.name, url: `${SITE}${pageUrl(collection, p.slug)}` })),
          },
          breadcrumbJsonLd(crumbs),
        ],
      },
    }),
    [meta, collection, crumbs]
  );
  useSeo(seo);

  return (
    <main data-testid={`${collection}-index-page`} className="min-h-screen bg-white text-[#0A0A0A]">
      <Navbar />
      <section className="pt-[9rem] lg:pt-[11rem]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 lg:pb-28">
          <Breadcrumbs items={crumbs} />
          <div className="mt-10 lg:mt-14 grid grid-cols-12 gap-y-6 lg:gap-x-6 pb-12 lg:pb-16">
            <div className="col-span-12 lg:col-span-3">
              <div className="overline">{meta.label}</div>
            </div>
            <div className="col-span-12 lg:col-span-9">
              <h1 data-testid="collection-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.02] text-balance">
                {meta.h1}
              </h1>
              <p className="mt-6 text-base lg:text-lg text-black/65 leading-relaxed max-w-2xl">{meta.intro}</p>
            </div>
          </div>

          <ul data-testid="collection-list" className="border-t hairline">
            {meta.items.map((p, i) => (
              <li key={p.slug} data-testid={`collection-item-${p.slug}`} className="group border-b hairline">
                <Link
                  to={pageUrl(collection, p.slug)}
                  className="grid grid-cols-12 gap-x-3 gap-y-3 lg:gap-6 items-center px-2 lg:px-6 py-7 lg:py-10 transition-colors hover:bg-[#0A0A0A] hover:text-white"
                >
                  <div className="col-span-2 lg:col-span-1 font-display text-2xl lg:text-3xl text-black/35 group-hover:text-white/50">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h2 className="col-span-10 lg:col-span-4 font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight">
                    {p.name}
                  </h2>
                  <p className="col-span-10 col-start-3 lg:col-span-5 lg:col-start-auto text-sm opacity-70 leading-relaxed">
                    {p.intro}
                  </p>
                  <div className="hidden lg:flex col-span-2 items-center justify-end gap-4">
                    <div className="w-28 h-20 overflow-hidden">
                      <img src={p.image.replace(".webp", "-sm.webp")} alt={p.name} loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" />
                    </div>
                    <ArrowUpRight size={18} className="opacity-40 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <CtaBand />
      <Footer />
    </main>
  );
}
